import AccessWrapper from "components/Modules/AccessWrapper";
import { useTranslation } from "react-i18next";
import AutoCompleteBox from "components/Elements/EMIChip/AutoCompleteBox";
import ColourConstants from "helpers/colourConstants";
import CommonModal from "./CommonModal";
import DeleteDialog from "components/Elements/DeleteDialog";
import GeneralButton from "components/Elements/GeneralButton";
import ImportFileDialouge from "./ImportFileDialog";
import ModalAwaitingImports from "./ModalAwaitingImports";
import ModelsListTable from "./ModelsListTable";
import RestoreModalDialog from "./RestoreModalDialog";
import RoleWrapper from "components/Modules/RoleWrapper";
import ShareModal from "./ShareModal";
import TabTitle from "components/Elements/TabTitle";
import Typography from "@mui/material/Typography";
import VersionListTable from "./VersionListTable";
import mainAccess from "helpers/access";
import roles from "helpers/roles";
import updateStorage from "helpers/updateStorage";
import useSuperAdminExclude from "hooks/useSuperAdminExclude";
import withMount from "components/HOC/withMount";
import { Apis } from "services/api";
import { ChipTypeEnums, ModelListChipTypeEnums } from "constants/enums";
import { CircularProgress, LinearProgress } from "@mui/material";
import { createTableSortHandler, defaultPageSize } from "helpers/utils";
import { FULL_ACCESS } from "constants/AccessTypes/AccessTypes";
import { getSiteApplicationDetail } from "services/clients/sites/siteApplications/siteApplicationDetails";
import { handleSiteAppClick } from "helpers/handleSiteAppClick";
import { makeStyles } from "tss-react/mui";
import { showError } from "../../../redux/common/actions";
import { useDispatch } from "react-redux";
import { useFilterInitialization } from "components/Elements/Filters/Dropdown/hooks/useFilterInitialization";
import { useOutletContext, useNavigate } from "react-router-dom";
import { modelImport } from "helpers/routePaths";
import { useRef } from "react";
import { useState, useCallback, useEffect, useMemo } from "react";
import {
	useInfiniteQuery,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import {
	addModal,
	duplicateAndTranslateModel,
	duplicateModal,
	getDeletedModels,
	getListOfModelVersions,
	getModelAutocompleteSearch,
	getModelImports,
	getModelList,
	getCountOfModelList,
	transferModel,
} from "services/models/modelList";
import {
	FilterContainer,
	FilterProvider,
	useFilters,
} from "components/Elements/Filters";
import { getValidDepartmentIds } from "helpers/departments";
import {
	makeGetNextPageParam,
	removeRowFromInfiniteCache,
	flattenPages,
} from "helpers/infiniteQuery";

const media = "@media(max-width: 414px)";

const useStyles = makeStyles()((theme) => ({
	listActions: {
		marginBottom: 30,
	},
	headerContainer: {
		display: "flex",
		[media]: {
			flexDirection: "column",
		},
	},
	headerText: {
		fontSize: 21,
	},
	buttonContainer: {
		display: "flex",
		marginLeft: "auto",
		flexWrap: "wrap",
		[media]: {
			marginLeft: 0,
		},
	},
	importButton: {
		background: "#ED8738",
		width: "50%",
	},
	productButton: {
		backgroundColor: ColourConstants.confirmButton,
		color: "#FFFFFF",
		fontFamily: "Roboto Condensed",
		width: 150,
		fontSize: "13.5px",
		fontWeight: "bold",
		marginRight: "10px",
	},
	loading: {
		position: "absolute",
		width: "100%",
		left: 0,
		top: 0,
	},
	filter: {
		display: "flex",
	},
}));

const debounce = (func, delay) => {
	let timer;
	return function () {
		let self = this;
		let args = arguments;
		clearTimeout(timer);
		timer = setTimeout(() => {
			func.apply(self, args);
		}, delay);
	};
};

const MODELS_LIST_KEY = ["models", "list"];
const MODELS_COUNT_KEY = ["models", "count"];

// Filter configuration for the new Filter framework
const filterConfig = {
	filters: {
		modelStatus: true,
		department: { multiSelect: true },
		tableSort: true,
		chips: {
			enabled: true,
			hiddenTypes: [
				ChipTypeEnums.SiteDepartmentID,
				ChipTypeEnums.ModelStatusID,
			],
		},
	},
};

const ModelListsContent = ({ getError, isMounted }) => {
	const { t } = useTranslation();
	const { classes } = useStyles();
	const { filterValues, updateFilter, allFiltersInitialized } = useFilters();
	const queryClient = useQueryClient();

	useSuperAdminExclude();

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [deleteID, setDeleteID] = useState(null);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [openVersionTableDialog, setOpenVersionTableDialog] = useState(false);
	const [openAddNewModal, setOpenAddNewModal] = useState(false);
	const [openDuplicateModal, setOpenDuplicateModal] = useState(false);
	const [openShareModel, setOpenShareModel] = useState(false);
	const [duplicateModalData, setDuplicateModalData] = useState({});
	const [shareModelData, setShareModellData] = useState({});
	const [modelVersions, setModelVersions] = useState([]);
	const [isVersionTableLoading, setVersionTableLoading] = useState(false);
	const [openImportFile, setOpenImportFile] = useState(false);
	const [openRestoreModal, setOpenRestoreModal] = useState(false);
	const [modelImportData, setModelImportData] = useState([]);
	const [deletedModelData, setDeletedModelData] = useState([]);

	const [searching, setSearching] = useState(false);
	const [activeModelVersion, setActiveModelVersion] = useState(null);
	const [siteAppState, setSiteAppState] = useState(null);

	const tableSortParams = filterValues.tableSort?.params || {
		sortField: "name",
		sortOrder: "asc",
	};
	const currentTableSort = [
		tableSortParams.sortField,
		tableSortParams.sortOrder,
	];

	const searchRef = useRef("");

	useFilterInitialization("chips", [], {
		getDefaultValue: () => [],
		getParams: (chips) => ({ chips }),
		waitForData: false,
	});

	const updatedChips = useMemo(() => {
		const currentChips = filterValues.chips?.value || [];
		const hiddenTypes = [ChipTypeEnums.SiteDepartmentID, ChipTypeEnums.ModelStatusID];
		const preservedChips = currentChips.filter(chip => !hiddenTypes.includes(chip.chipType));
		const departmentValue = filterValues.department?.value;
		const validDeptIds = getValidDepartmentIds(departmentValue);
		const departmentChips = validDeptIds.map(id => ({
			chipType: ChipTypeEnums.SiteDepartmentID,
			id,
			value: "",
		}));
		return [...preservedChips, ...departmentChips];
	}, [filterValues.chips?.value, filterValues.department?.value]);

	const chips = updatedChips;

	const [isRemovingChip, setIsRemovingChip] = useState(false);
	const [dataForFetchingDefect, setDataForFetchingDefect] = useState({
		pageNumber: 1,
		pageSize: defaultPageSize(),
		search: "",
		sortField: "",
		sort: "",
	});
	const [autocompleteData, setAutocompleteData] = useState([]);
	const tableRef = useRef(null);

	const handleAutoCompleteContent = async (item) => {
		const mappedChip = {
			chipType:
				item.chipType ||
				(item.type === "modelId"
					? ChipTypeEnums.ModelID
					: item.type === "siteAssetId"
					? ChipTypeEnums.SiteAssetID
					: item.type === "modelTypeId"
					? ChipTypeEnums.ModelTypeID
					: ChipTypeEnums.Undefined),
			id: item.id || null,
			value: item.value || "",
		};

		const currentChips = filterValues.chips?.value || [];
		const isDuplicate = currentChips.some(
			(existingItem) =>
				existingItem.id === mappedChip.id &&
				(existingItem.chipType === mappedChip.chipType ||
					existingItem.type === item.type) &&
				existingItem.value === mappedChip.value
		);

		if (!isDuplicate) {
			const updatedChipsLocal = [...currentChips, mappedChip];
			updateFilter("chips", updatedChipsLocal, { chips: updatedChipsLocal });
		}

		setAutocompleteData([]);
		setDataForFetchingDefect({
			pageNumber: 1,
			pageSize: defaultPageSize(),
			search: "",
			sortField: "",
			sort: "",
		});

		searchRef.current = "";
	};

	const handleRemoveChip = async (removedChip) => {
		setIsRemovingChip(true);
		try {
			const currentChips = filterValues.chips?.value || [];
			const updatedChipsLocal = currentChips.filter((item) => {
				return !(
					item.id === removedChip.id && item.chipType === removedChip.chipType
				);
			});
			updateFilter("chips", updatedChipsLocal, { chips: updatedChipsLocal });
		} finally {
			setIsRemovingChip(false);
		}
	};

	const me =
		JSON.parse(sessionStorage.getItem("me")) ||
		JSON.parse(localStorage.getItem("me"));
	const {
		position,
		application,
		customCaptions,
		siteAppID,
		site: { clientID },
	} = me;

	const [shareModelsFromApi, setShareModelsFromApi] = useState(0);
	useEffect(() => {
		const fetchClient = async () => {
			await dispatch(handleSiteAppClick(siteAppID, true));
			setShareModelsFromApi(application.shareModels);
		};
		fetchClient();
	}, [clientID, application?.id, application?.shareModels, siteAppID]);

	const isSharableModel = shareModelsFromApi === 1 || shareModelsFromApi === 2;
	const { access } = useOutletContext();

	const displayError = useCallback(
		(errorMessage, response) =>
			dispatch(
				showError(
					response?.data?.detail ||
						errorMessage ||
						t("message.somethingWentWrong")
				)
			),
		[dispatch, t]
	);

	// Page size frozen at mount.
	const [pageSize] = useState(() => defaultPageSize());

	// Inputs that shape the API call — frozen into the queryKey so filter
	// changes refetch automatically.
	const queryInputs = useMemo(() => {
		const modelStatusID = filterValues["modelStatus"]?.value?.id || "";
		const departmentParams = filterValues["department"]?.params || {};
		const allowedChips = chips.filter((chip) =>
			Object.values(ModelListChipTypeEnums).includes(chip.chipType)
		);
		return {
			id: position?.siteAppID || 24,
			modelStatusId: modelStatusID,
			siteDepartmentId: departmentParams.siteDepartmentId || "",
			siteDepartmentIds: departmentParams.siteDepartmentIds || [],
			chips: allowedChips,
			sortField: tableSortParams.sortField || "",
			sortOrder: tableSortParams.sortOrder || "asc",
		};
	}, [
		position?.siteAppID,
		filterValues["modelStatus"]?.value?.id,
		filterValues["department"]?.params,
		chips,
		tableSortParams.sortField,
		tableSortParams.sortOrder,
	]);

	const modelsQueryKey = useMemo(
		() => [...MODELS_LIST_KEY, { ...queryInputs, pageSize }],
		[queryInputs, pageSize]
	);

	const modelsQuery = useInfiniteQuery({
		queryKey: modelsQueryKey,
		enabled: allFiltersInitialized && Boolean(position?.siteAppID || 24),
		initialPageParam: 1,
		queryFn: async ({ pageParam }) => {
			const response = await getModelList({
				...queryInputs,
				pageNumber: pageParam,
				pageSize,
				search: searchRef.current,
			});
			if (!response?.status) {
				throw new Error(
					response?.data?.errors?.siteAppId?.[0] ||
						t("message.failedToLoadModels")
				);
			}
			const rows = (response.data || []).map((d) => ({
				...d,
				serialNumberRange: d?.serialNumberRange ?? "",
				fullName:
					d?.name +
					" " +
					(d?.modelName === null || d?.modelName === undefined
						? ""
						: d?.modelName),
			}));
			return { rows, pageParam };
		},
		getNextPageParam: makeGetNextPageParam(pageSize),
	});

	useEffect(() => {
		if (modelsQuery.isError) {
			displayError(modelsQuery.error?.message, null);
		}
	}, [modelsQuery.isError, modelsQuery.error, displayError]);

	const countQueryKey = useMemo(
		() => [...MODELS_COUNT_KEY, queryInputs],
		[queryInputs]
	);

	const countQuery = useQuery({
		queryKey: countQueryKey,
		enabled: allFiltersInitialized && Boolean(position?.siteAppID || 24),
		queryFn: async () => {
			const response = await getCountOfModelList({
				...queryInputs,
				search: searchRef.current,
			});
			return response?.status ? response.data : 0;
		},
	});

	const allData = useMemo(() => flattenPages(modelsQuery.data), [modelsQuery.data]);
	const countOfModel = countQuery.data ?? 0;
	const isLoading = modelsQuery.isLoading && allFiltersInitialized;

	const refreshModels = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: MODELS_LIST_KEY });
		return queryClient.invalidateQueries({ queryKey: MODELS_COUNT_KEY });
	}, [queryClient]);

	const handleDeleteDialogOpen = (id) => {
		setDeleteID(id);
		setOpenDeleteDialog(true);
	};

	const closeDeleteDialog = () => setOpenDeleteDialog(false);

	const handleRemoveData = (id) => {
		removeRowFromInfiniteCache(queryClient, modelsQueryKey, (row) => row.id === id);
		queryClient.invalidateQueries({ queryKey: MODELS_COUNT_KEY });
	};

	const handleSearch = debounce(
		async (searchValue) => {
			setSearching(true);
			searchRef.current = searchValue;
			const autoCompleteRes = await getModelAutocompleteSearch({
				search: searchValue,
			});
			setAutocompleteData(autoCompleteRes?.data);
			if (!searchValue || searchValue === "")
				setDataForFetchingDefect({
					pageNumber: 1,
					pageSize: defaultPageSize(),
					search: "",
					sortField: "",
					sort: "",
				});
			setSearching(false);
		},
		[1000]
	);

	const onDuplicateModalOpen = (modalToDuplicate) => {
		setDuplicateModalData(modalToDuplicate);
		setOpenDuplicateModal(true);
	};
	const onDuplicateAndTranslateModalOpen = async (row) => {
		const response = await duplicateAndTranslateModel({
			modelId: row?.id,
			modelVersionId: row?.devModelVersionID,
		});

		if (response?.status) {
			navigate(`${modelImport}/${response.data}`);
		} else {
			dispatch(
				showError(
					response?.data?.detail ||
						response?.data?.errors?.modelId?.[0] ||
						response?.data?.errors?.modelVersionId?.[0] ||
						t("message.couldNotStartDuplicateAndTranslate")
				)
			);
		}
	};
	const onShareModelOpen = (modalToShare) => {
		setShareModellData(modalToShare);
		setOpenShareModel(true);
	};

	const createModal = async (payload) => {
		return await addModal(payload);
	};

	const onDuplicateModal = async (payload) => {
		return await duplicateModal({
			modelId: duplicateModalData?.id,
			modelVersionId: duplicateModalData?.devModelVersionID,
			name: payload?.name,
			modelName: payload?.modelName,
			modelTypeId: payload?.modelTypeID,
			type: payload?.type,
			serialNumberRange: payload?.serialNumberRange,
		});
	};
	const onShareModel = async (payload) => {
		return await transferModel(payload);
	};

	const onViewVersionTableOpen = async (id) => {
		setOpenVersionTableDialog(true);
		setVersionTableLoading(true);
		setActiveModelVersion(
			allData.find((model) => model.id === id)?.activeModelVersion
		);

		const response = await getListOfModelVersions(id);

		if (response.status) {
			setModelVersions(response.data);
		} else {
			displayError(response?.data?.errors?.modelId[0], response);
			setOpenVersionTableDialog(false);
		}

		setVersionTableLoading(false);
	};

	const fetchModelImports = useCallback(async () => {
		const response = await getModelImports(position?.siteAppID || 24);
		if (!isMounted.aborted) {
			if (response.status) {
				setModelImportData(response?.data);
			} else {
				displayError(response?.data?.errors?.siteAppId[0], response);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fetchDeletedModels = useCallback(async () => {
		const response = await getDeletedModels();
		if (!isMounted.aborted) {
			if (response.status) {
				setDeletedModelData(response?.data);
			} else {
				displayError(response?.data?.errors?.siteAppId[0], response);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fetchSiteApplicationDetails = async () => {
		const result = await getSiteApplicationDetail(siteAppID);
		setSiteAppState(result);
	};

	useEffect(() => {
		fetchSiteApplicationDetails();
		if (siteAppID) updateStorage(siteAppID);
		fetchModelImports();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (access === FULL_ACCESS && position?.allowPublish) {
			fetchDeletedModels();
		}
	}, [access, position?.allowPublish, fetchDeletedModels]);

	useEffect(() => {
		const t = setTimeout(() => {
			fetchModelImports();
		}, 2000);
		return () => clearTimeout(t);
	}, [fetchModelImports]);

	useEffect(() => {
		const hasInProgress = (modelImportData || []).some(
			(r) => r.translationStatus === "InProgress"
		);
		if (!hasInProgress) return;

		const id = setInterval(() => {
			fetchModelImports();
		}, 5000);
		return () => clearInterval(id);
	}, [modelImportData, fetchModelImports]);

	// When a translation finishes successfully the import row disappears from
	// modelImportData. Refetch the main model list so the new row shows up
	// without a manual refresh.
	const previousImportsRef = useRef([]);
	useEffect(() => {
		const previousInProgressIds = previousImportsRef.current
			.filter((r) => r.translationStatus === "InProgress")
			.map((r) => r.id);
		const currentIds = new Set((modelImportData || []).map((r) => r.id));
		const justFinished = previousInProgressIds.filter(
			(id) => !currentIds.has(id)
		);

		if (justFinished.length > 0) {
			refreshModels();
		}

		previousImportsRef.current = modelImportData || [];
	}, [modelImportData, refreshModels]);

	const ModelListHeader = [
		customCaptions?.modelTemplate,
		customCaptions?.modelType,
		t("label.status"),
		t("label.latestVersion"),
		t("label.activeVersion"),
		customCaptions?.departmentPlural ?? "Departments",
		t("label.dateCreated"),
		t("label.modifiedBy"),
		t("label.dateModified"),
		t("label.reviewDate"),
	];

	const DeletedModelListHeader = [
		customCaptions?.modelTemplate,
		customCaptions?.modelType,
		// "Restore" is a sentinel matched via header.toLowerCase() === "restore"
		// in RestoreModalDialog (rendered as empty), not user-visible text.
		"Restore",
	];
	const DeletedModelListColumn = ["name", "modelType", "Restore"];
	const ModelListColumn = [
		"fullName",
		"modelType",
		"status",
		"devModelVersion",
		"activeModelVersion",
		"departments",
		"createdDate",
		"modifiedUser",
		"modifiedDate",
		"reviewDate",
	];

	useEffect(() => {
		if (dataForFetchingDefect?.search.length >= 3) {
			setSearching(true);
			handleSearch(dataForFetchingDefect?.search);
		} else {
			setAutocompleteData([]);
		}
	}, [dataForFetchingDefect?.search]);

	if (siteAppState?.data?.application?.showSerialNumberRange) {
		ModelListHeader.splice(5, 0, customCaptions?.serialNumberRange);
		ModelListColumn.splice(5, 0, "serialNumberRange");
	}

	const handleTableSort = useCallback(
		(sortField, sortOrder) => {
			createTableSortHandler(tableSortParams, updateFilter)(
				sortField,
				sortOrder
			);
		},
		[tableSortParams, updateFilter]
	);

	const tableIsRefreshing =
		(modelsQuery.isFetching && !modelsQuery.isFetchingNextPage) ||
		countQuery.isFetching;

	return (
		<div className="container">
			<TabTitle title={`${customCaptions.model} | ${application.name}`} />
			<CommonModal
				open={openAddNewModal}
				closeHandler={() => setOpenAddNewModal(false)}
				siteId={position?.siteAppID}
				data={null}
				title={t("action.add") + " "}
				createProcessHandler={createModal}
			/>
			<CommonModal
				open={openDuplicateModal}
				closeHandler={() => setOpenDuplicateModal(false)}
				siteId={position?.siteAppID}
				data={duplicateModalData}
				title={t("action.duplicate") + " "}
				createProcessHandler={onDuplicateModal}
				isDuplicate
			/>
			<ShareModal
				open={openShareModel}
				closeHandler={() => setOpenShareModel(false)}
				siteId={position?.siteAppID}
				data={shareModelData}
				title={t("action.transfer") + " "}
				createProcessHandler={onShareModel}
				isSharableModel={shareModelsFromApi}
			/>
			<DeleteDialog
				entityName="Model"
				open={openDeleteDialog}
				closeHandler={closeDeleteDialog}
				deleteID={deleteID}
				deleteEndpoint={Apis.Models}
				deleteMsg={t("message.deleteModelWarning")}
				handleRemoveData={handleRemoveData}
			/>
			<VersionListTable
				open={openVersionTableDialog}
				isLoading={isVersionTableLoading}
				closeHandler={() => setOpenVersionTableDialog(false)}
				versions={modelVersions}
				activeModelVersion={activeModelVersion}
			/>
			<ImportFileDialouge
				open={openImportFile}
				handleClose={() => setOpenImportFile(false)}
				importSuccess={fetchModelImports}
				getError={getError}
				siteAppID={position?.siteAppID}
			/>
			<RestoreModalDialog
				open={openRestoreModal}
				handleClose={() => setOpenRestoreModal(false)}
				importSuccess={fetchDeletedModels}
				getError={getError}
				headers={DeletedModelListHeader}
				columns={DeletedModelListColumn}
				data={deletedModelData}
				isMounted={isMounted}
			/>
			<div className={classes.listActions}>
				<div className={classes.headerContainer}>
					<Typography
						className={classes.headerText}
						component="h1"
						gutterBottom
					>
						{countOfModel === 0 ? (
							<strong>
								{t("label.entityList", { entity: customCaptions?.model })}
							</strong>
						) : (
							<strong>
								{t("label.entityListCount", {
									entity: customCaptions?.model,
									count: countOfModel,
								})}
							</strong>
						)}
					</Typography>

					<div className={classes.buttonContainer}>
						{access === FULL_ACCESS && deletedModelData?.length > 0 && position?.allowPublish && (
							<GeneralButton
								style={{ backgroundColor: "#ed8738" }}
								onClick={() => setOpenRestoreModal(true)}
							>
								{t("action.restoreModel")}
							</GeneralButton>
						)}
						<RoleWrapper roles={[roles.siteUser]}>
							<AccessWrapper access={access} accessList={["E"]}>
								<GeneralButton
									style={{ backgroundColor: "#ed8738" }}
									onClick={() => setOpenImportFile(true)}
								>
									{t("action.importFromExisting")}
								</GeneralButton>
							</AccessWrapper>
						</RoleWrapper>

						<AccessWrapper access={access} accessList={["E"]}>
							<GeneralButton
								style={{ backgroundColor: "#23bb79" }}
								onClick={() => setOpenAddNewModal(true)}
							>
								{t("action.addNew")}
							</GeneralButton>
						</AccessWrapper>
					</div>
				</div>
				<ModalAwaitingImports
					modelImportData={modelImportData}
					customCaptions={customCaptions}
					isReadOnly={position?.[mainAccess.modelAccess] === "R"}
				/>
				<AutoCompleteBox
					searchQuery={dataForFetchingDefect?.search}
					setSearchQuery={(e) => {
						e.persist();
						setDataForFetchingDefect((prev) => ({
							...prev,
							search: e.target.value,
						}));
					}}
					width="100%"
					required
					label={t("label.modelAccess")}
					setSelectedValue={handleAutoCompleteContent}
					selectdValueToshow="name"
					dataSource={autocompleteData}
					isSearching={searching}
					chips={chips}
				/>

				{/* Filter Framework */}
				<div style={{ marginTop: 20 }}>
					<FilterContainer />
				</div>
			</div>
			{(searching || isRemovingChip || tableIsRefreshing) && <LinearProgress />}
			{isLoading ? (
				<CircularProgress />
			) : (
				<ModelsListTable
					ref={tableRef}
					position={position}
					data={allData}
					access={access}
					headers={ModelListHeader}
					columns={ModelListColumn}
					handleSort={handleTableSort}
					handleDeleteDialogOpen={handleDeleteDialogOpen}
					handleDuplicateModalOpen={onDuplicateModalOpen}
				handleDuplicateAndTranslateModalOpen={onDuplicateAndTranslateModalOpen}
					handleShareModelOpen={onShareModelOpen}
					handleViewVersionModalOpen={onViewVersionTableOpen}
					isSharableModel={isSharableModel}
					currentTableSort={currentTableSort}
					hasNextPage={modelsQuery.hasNextPage}
					isFetchingNextPage={modelsQuery.isFetchingNextPage}
					fetchNextPage={modelsQuery.fetchNextPage}
					isFetchNextPageError={modelsQuery.isFetchNextPageError}
				/>
			)}
		</div>
	);
};

// Wrapper component that provides FilterContext
const ModelLists = (props) => {
	return (
		<FilterProvider config={filterConfig} storageKey="models">
			<ModelListsContent {...props} />
		</FilterProvider>
	);
};

export default withMount(ModelLists);
